import type { ZodType } from 'zod'

import { ApiError, messageFromProblem } from './api-error'
import { apiConfig } from './config'

type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
  /** Query-string values. `undefined`, `null` and empty strings are left out. */
  query?: Record<string, QueryValue>
  /** Sent as JSON, or as multipart when it is a `FormData` (the browser sets the boundary itself). */
  body?: unknown
  /** Pass TanStack Query's `signal` so a superseded request is really cancelled. */
  signal?: AbortSignal
  headers?: Record<string, string>
  /** Overrides the default timeout for this call. */
  timeoutMs?: number
}

/**
 * The one place that talks HTTP. It builds the URL, sends JSON, applies a timeout, understands the backend's error bodies,
 * and checks the response against a zod schema, so what comes back is exactly the documented shape or an `ApiError`.
 * It never retries by itself: a POST that timed out may still have worked, so retrying is TanStack Query's decision (and
 * mutations do not retry).
 */
const isForm = (body: unknown): body is FormData => typeof FormData !== 'undefined' && body instanceof FormData

export class HttpClient {
  get<T>(path: string, schema: ZodType<T>, options: RequestOptions = {}) {
    return this.request('GET', path, schema, options)
  }

  post<T>(path: string, schema: ZodType<T>, options: RequestOptions = {}) {
    return this.request('POST', path, schema, options)
  }

  put<T>(path: string, schema: ZodType<T>, options: RequestOptions = {}) {
    return this.request('PUT', path, schema, options)
  }

  delete<T>(path: string, schema: ZodType<T>, options: RequestOptions = {}) {
    return this.request('DELETE', path, schema, options)
  }

  async request<T>(method: 'GET' | 'POST' | 'PUT' | 'DELETE', path: string, schema: ZodType<T>, options: RequestOptions = {}): Promise<T> {
    const url = this.url(path, options.query)
    const timeout = AbortSignal.timeout(options.timeoutMs ?? apiConfig.timeoutMs)
    const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout

    let response: Response
    try {
      response = await fetch(url, {
        method,
        signal,
        headers: { Accept: 'application/json', ...(options.body !== undefined && !isForm(options.body) && { 'Content-Type': 'application/json' }), ...options.headers },
        ...(options.body !== undefined && { body: isForm(options.body) ? options.body : JSON.stringify(options.body) }),
      })
    } catch (error) {
      if (options.signal?.aborted) throw new ApiError('The request was cancelled.', { code: 'CANCELLED' })
      if (timeout.aborted || (error instanceof DOMException && error.name === 'TimeoutError')) {
        throw new ApiError('The server took too long to respond. Please try again.', { code: 'TIMEOUT' })
      }
      throw new ApiError('Could not reach the server. Check your connection and try again.', { code: 'NETWORK_ERROR' })
    }

    const requestId = response.headers.get('X-Correlation-ID')
    const text = await response.text()
    let body: unknown = null
    try {
      body = text ? JSON.parse(text) : null
    } catch {
      body = null
    }

    if (!response.ok) throw new ApiError(messageFromProblem(body, response.status), { code: 'HTTP_ERROR', status: response.status, requestId, details: body })

    const parsed = schema.safeParse(body)
    if (!parsed.success) {
      throw new ApiError('The server answered with something unexpected.', { code: 'INVALID_RESPONSE', status: response.status, requestId, details: parsed.error.issues })
    }
    return parsed.data
  }

  /**
   * Opens a long-lived response (a server-sent event stream) and hands it back unread. There is no timeout, because a stream stays
   * open on purpose; the caller's `signal` ends it. A non-2xx answer is an `ApiError` as usual.
   */
  async open(path: string, options: Pick<RequestOptions, 'query' | 'signal' | 'headers'> = {}): Promise<Response> {
    let response: Response
    try {
      response = await fetch(this.url(path, options.query), { method: 'GET', signal: options.signal, headers: { Accept: 'text/event-stream', ...options.headers } })
    } catch {
      if (options.signal?.aborted) throw new ApiError('The request was cancelled.', { code: 'CANCELLED' })
      throw new ApiError('Could not reach the server. Check your connection and try again.', { code: 'NETWORK_ERROR' })
    }
    if (!response.ok) {
      let body: unknown = null
      try {
        body = JSON.parse(await response.text())
      } catch {
        body = null
      }
      throw new ApiError(messageFromProblem(body, response.status), { code: 'HTTP_ERROR', status: response.status, requestId: response.headers.get('X-Correlation-ID'), details: body })
    }
    return response
  }

  /**
   * Downloads a file as a `Blob` (an image, say). The answer is the file's bytes, not JSON, so there is no schema; a non-2xx answer
   * is an `ApiError` as usual, and a slow download is given more time than a JSON call.
   */
  async download(path: string, options: Pick<RequestOptions, 'query' | 'signal' | 'headers' | 'timeoutMs'> = {}): Promise<Blob> {
    const timeout = AbortSignal.timeout(options.timeoutMs ?? 60_000)
    const signal = options.signal ? AbortSignal.any([options.signal, timeout]) : timeout
    let response: Response
    try {
      response = await fetch(this.url(path, options.query), { method: 'GET', signal, headers: { Accept: 'application/octet-stream', ...options.headers } })
    } catch (error) {
      if (options.signal?.aborted) throw new ApiError('The request was cancelled.', { code: 'CANCELLED' })
      if (timeout.aborted || (error instanceof DOMException && error.name === 'TimeoutError')) throw new ApiError('The download took too long. Please try again.', { code: 'TIMEOUT' })
      throw new ApiError('Could not reach the server. Check your connection and try again.', { code: 'NETWORK_ERROR' })
    }
    if (!response.ok) {
      let body: unknown = null
      try {
        body = JSON.parse(await response.text())
      } catch {
        body = null
      }
      throw new ApiError(messageFromProblem(body, response.status), { code: 'HTTP_ERROR', status: response.status, requestId: response.headers.get('X-Correlation-ID'), details: body })
    }
    return response.blob()
  }

  private url(path: string, query: RequestOptions['query']): string {
    const params = new URLSearchParams()
    for (const [key, value] of Object.entries(query ?? {})) if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
    const search = params.size ? `?${params}` : ''
    return `${apiConfig.baseUrl()}${path.startsWith('/') ? '' : '/'}${path}${search}`
  }
}

/** The shared client every service uses. */
export const httpClient = new HttpClient()
